import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { MotifTabNavigation, MotifTabControl } from '@ey-xd/motif-react';
import { Link, Navigate } from 'react-router';
import { reduxForm, reset } from 'redux-form';
import { withRouter } from '../../common/withRouter';
import SignOffHeader from '../../components/bulkPeriodManagement/SignOffHeader';
import * as commonActions from '../../actions/commonActions';
import SignOffTable from '../../components/bulkPeriodManagement/SignOffTable';
import * as bulkProcessManagementActions from '../../actions/bulkProcessManagementActions';
import { store } from '../../store/configureStore';
import * as reitActions from '../../actions/reitActions';
import * as authActions from '../../actions/authActions';
import * as Constants from '../../constants/other';
import * as actions from '../../actions/actionTypes';
import BulkSignOffModal from '../../components/bulkPeriodManagement/BulkSignOffModal';
import * as clientActions from '../../actions/clientActions';
import * as periodActions from '../../actions/periodActions';

/**
 * MasterDataManagementTRSContainer and SignOffContainer components
 * @extends {React.Component}
 */
class SignOffContainer extends Component {
  /**
   * Creates a new MasterDataManagementTRSContainer (SignOffContainer)
   * @constructor
   * @param {Object} props The component properties
   * @param {Object} context The component context
   */
  constructor(props, context) {
    super(props, context);
    this.handleGridChanges = this.handleGridChanges.bind(this);
    this.setGridColumnState = this.setGridColumnState.bind(this);
    this.handleGridColumnStateChange =
      this.handleGridColumnStateChange.bind(this);
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleSignOffSave = this.handleSignOffSave.bind(this);
    this.toggleShowSignOffModal = this.toggleShowSignOffModal.bind(this);
    this.fetchReportPeriodsByClientId =
      this.fetchReportPeriodsByClientId.bind(this);
    this.state = {
      gridCurrentState: [],
      gridColumnState: [],
      bulkProcessManagementData: [],
      selectedCalendarYear: null,
      selectedQuarter: null,
      resetSignOffRadioButtons: false,
      diagnosticMessage: '',
      bulkProcessActionId: null,
      defaultReportPeriod: null,
      reportPeriods: [],
      isAudit: false,
    };
  }

  /**
   * Invoked immediately after mounting occurs
   * @returns {undefined}
   */
  componentDidMount() {
    // Get available REITs in a report period then set the calendar year and current quarter
    this.fetchReportPeriodsByClientId();
    this.setGridColumnState();
    this.getClientData();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.bulkProcessManagementData &&
      this.props.bulkProcessManagementData !==
        prevProps.bulkProcessManagementData
    ) {
      this.setState({
        bulkProcessManagementData: this.props.bulkProcessManagementData,
      });
    }
  }

  componentWillUnmount() {
    this.setState({ bulkProcessManagementData: [] });

    // Clearing the redux state for bulk process management data
    this.props.bulkProcessManagementActions.fetchBulkProcessManagementData();
  }

  getClientData() {
    this.props.clientActions.loadClient(this.props.clientId).then(() => {
      this.setState({ isAudit: this.props.client.isAudit });
      if (this.props.client.isAudit) {
        this.props.router.navigate(
          `/client/${this.props.clientId}/bulkPeriodManagement`
        );
      }
    });
  }

  handleReportPeriodSelection = (reportPeriod) => {
    if (reportPeriod === null || typeof reportPeriod !== 'string') {
      return;
    }
    // Extract quarter and year from report period string (format: "Q1 2023")
    const parts = reportPeriod.split(' ');
    const quarter = parts[0]; // e.g., "Q1"
    const year = parseInt(parts[1]); // e.g., 2023
    this.setState(
      {
        selectedQuarter: quarter,
        selectedCalendarYear: year,
        defaultReportPeriod: reportPeriod,
      },
      this.getSignOffGridData
    );
  };

  getSignOffGridData() {
    this.props.bulkProcessManagementActions
      .fetchBulkProcessManagementData(
        this.props.clientId,
        this.state.selectedCalendarYear, // Use the extracted year
        this.state.selectedQuarter // Use the extracted quarter
      )
      .then(() => {
        this.setState({
          bulkProcessManagementData: this.props.bulkProcessManagementData,
        });
      });
  }

  handleSignOffSave(selectedOption) {
    // Set the Bulk Process Action ID to 1
    const requestedAction = Constants.updateSignOffStatusAction.find(
      (x) => x.value == selectedOption
    ).action;
    this.setState(
      {
        bulkProcessActionId: selectedOption,
        selectedSignOffStatus: requestedAction,
      },
      this.initiateBulkProcess
    );
  }

  toggleShowSignOffModal() {
    this.setState({
      showSignOffModal: !this.state.showSignOffModal,
      resetSignOffRadioButtons: !this.state.resetSignOffRadioButtons,
    });
  }

  getBulkProcessRequestJSON() {
    return {
      requestedSignOffStatus: this.state.selectedSignOffStatus,
    };
  }

  fetchReportPeriodsByClientId() {
    this.props.periodActions
      .fetchReportPeriodsByClientId(this.props.clientId)
      .then((periods) => {
        let defaultReportPeriod = null;
        if (periods && periods.length > 0) {
          const currentDate = new Date();
          const currentQuarter = `Q${Math.ceil((currentDate.getMonth() + 1) / 3)} ${currentDate.getFullYear()}`;

          // Parse endDate once for each period
          const periodsWithParsedDate = periods.map((rp) => ({
            ...rp,
            parsedEndDate: new Date(rp.endDate),
          }));

          // Sort periods in descending order by parsedEndDate
          const periodsInSortedOrder = [...periodsWithParsedDate].sort(
            (a, b) => b.parsedEndDate - a.parsedEndDate
          );

          // Find the max report period
          const maxReportPeriod =
            periodsInSortedOrder.length > 0 ? periodsInSortedOrder[0] : null;

          // Check if the current quarter exists in periods
          const currentQuarterPeriod = periodsInSortedOrder.find(
            (rp) => rp.reportPeriodDescription === currentQuarter
          );

          if (currentQuarterPeriod) {
            defaultReportPeriod = currentQuarterPeriod.reportPeriodDescription;
          } else {
            // Find the nearest future period (parsedEndDate > today), sorted ascending
            const futurePeriods = periodsWithParsedDate
              .filter((rp) => rp.parsedEndDate > currentDate)
              .sort((a, b) => a.parsedEndDate - b.parsedEndDate);
            if (futurePeriods.length > 0) {
              defaultReportPeriod = futurePeriods[0].reportPeriodDescription;
            } else {
              // Fallback to the latest available period
              defaultReportPeriod = maxReportPeriod.reportPeriodDescription;
            }
          }
          this.setState(
            {
              reportPeriods: periodsInSortedOrder,
            },
            () => this.handleReportPeriodSelection(defaultReportPeriod)
          );
        }
      });
  }

  initiateBulkProcess() {
    const request = {
      clientId: this.props.clientId,
      calendarYear: this.state.selectedCalendarYear,
      quarterDescription: this.state.selectedQuarter,
      reitIds: this.getSelectedReits(),
      bulkProcessActionId: this.state.bulkProcessActionId,
      diagnosticMessage: this.state.diagnosticMessage,
      bulkProcessRequestJSON: this.getBulkProcessRequestJSON(),
    };

    const currentGridState = [...this.state.gridCurrentState];
    const currentBulkProcessManagementDataState = [
      ...this.state.bulkProcessManagementData,
    ];

    this.updateCheckboxsState(request.reitIds);

    this.props.bulkProcessManagementActions
      .initiateBulkProcess(request)
      .then((isErrored) => {
        if (isErrored) {
          this.setState({
            currentGridState: currentGridState,
            bulkProcessManagementData: currentBulkProcessManagementDataState,
          });
        }
      })
      .catch((isErrored) => {
        if (isErrored) {
          this.setState({
            currentGridState: currentGridState,
            bulkProcessManagementData: currentBulkProcessManagementDataState,
          });
        }
      });

    this.resetModalsToggleState();
  }

  updateCheckboxsState(reitsToBeUpdated) {
    const rowData = this.state.gridCurrentState.map((row) => {
      if (reitsToBeUpdated.find((r) => r === row.reitID)) {
        return {
          ...row,
          isReitAvailableForSelection: false,
          reitSelection: false,
        };
      } else {
        return row;
      }
    });

    const updatedBulkProcessManagementData =
      this.state.bulkProcessManagementData.map((data) => {
        if (reitsToBeUpdated.find((r) => r === data.reitid)) {
          return {
            ...data,
            isReitAvailableForSelection: false,
            reitSelection: false,
          };
        } else {
          return data;
        }
      });

    this.props.bulkProcessManagementActions.dispatchBulkProcessData(
      updatedBulkProcessManagementData,
      actions.LOAD_BULK_PROCESS_MANAGEMENT_DATA_REFRESH
    );
    this.setState({ gridCurrentState: rowData });
  }

  resetModalsToggleState() {
    this.setState({ showServiceScopeModal: false, showSignOffModal: false });
  }

  //Set tab related state variables
  setActiveTab(currentIndex) {
    this.setState({ tabBar1: currentIndex });
  }

  //Set current tab index based
  handleTabClick(index) {
    this.setActiveTab(index);
  }

  /**
   * Set AG-Grid column state
   * @returns {undefined}
   */
  setGridColumnState() {
    var columnState = localStorage.getItem('REITSuiteSignOffColumnState');
    if (columnState) {
      this.setState({ gridColumnState: JSON.parse(columnState) });
    } else {
      this.setState({ gridColumnState: [{ colId: '-1' }] });
    }
  }

  /**
   * Handle Grid column state change
   * @returns {undefined}
   */
  handleGridColumnStateChange(columnState) {
    this.setState({ gridColumnState: columnState });
  }

  handleGridChanges(gridRowData) {
    const rowData =
      gridRowData &&
      gridRowData.length &&
      gridRowData.map((x) => {
        return {
          reitId: x.reitid,
          reitName: x.reitName,
          signOffStatus: x.signOffStatus,
          isReitAvailableForSelection: x.isReitAvailableForSelection,
          reitSelection: x.isReitAvailableForSelection
            ? x.reitSelection
            : false,
        };
      });

    this.setState({ gridCurrentState: rowData });
  }

  getSelectedReits() {
    return this.state.gridCurrentState
      .filter((r) => r.reitSelection)
      .map((r) => r.reitId);
  }

  resetForm() {
    store.dispatch(reset('signOffForm'));
  }

  render() {
    const { bulkProcessManagementData } = this.state;
    const noData =
      !bulkProcessManagementData || bulkProcessManagementData.length === 0;
    const { defaultReportPeriod } = this.state;
    const noReit = !defaultReportPeriod?.length;

    return (
      <div>
        <h2>Bulk Period Management</h2>
        <MotifTabNavigation variant="default">
          <Link
            to={`/client/${this.props.clientId}/bulkPeriodManagement`}
            tabIndex="-1"
          >
            <MotifTabControl
              role="tab"
              data-testid="tbNewPeriod"
              onClick={() => this.handleTabClick(1)}
            >
              New Period
            </MotifTabControl>
          </Link>
          {/* Conditional rendering for Services Scope tab */}
          {!this.props.isAudit ? (
            <Link
              to={`/client/${this.props.clientId}/bulkPeriodManagement/servicesScope`}
              tabIndex="-1"
            >
              <MotifTabControl
                role="tab"
                data-testid="tbServicesScope"
                onClick={() => this.handleTabClick(2)}
              >
                Services Scope
              </MotifTabControl>
            </Link>
          ) : (
            <></> // Return an empty fragment instead of null
          )}
          <Link
            to={`/client/${this.props.clientId}/bulkPeriodManagement/ownership`}
            tabIndex="-1"
          >
            <MotifTabControl
              role="tab"
              data-testid="tbOwnership"
              onClick={() => this.handleTabClick(3)}
            >
              Ownership %
            </MotifTabControl>
          </Link>
          <Link
            to={`/client/${this.props.clientId}/bulkPeriodManagement/tenantServicesAmenities`}
            tabIndex="-1"
          >
            <MotifTabControl
              role="tab"
              data-testid="tbTenantServices"
              onClick={() => this.handleTabClick(4)}
            >
              Tenant Services / Amenities
            </MotifTabControl>
          </Link>
          <Link
            to={`/client/${this.props.clientId}/bulkPeriodManagement/signOff`}
            tabIndex="-1"
          >
            <MotifTabControl
              role="tab"
              data-testid="tbSignOff"
              className="motif-active"
              onClick={() => this.handleTabClick(5)}
            >
              Sign Off
            </MotifTabControl>
          </Link>
        </MotifTabNavigation>

        {noReit ? (
          <div className="mt-3">No REITs found for the selected client.</div>
        ) : (
          <SignOffHeader
            gridCurrentState={this.state.gridCurrentState}
            toggleShowSignOffModal={this.toggleShowSignOffModal}
            clientId={this.props.clientId}
            handleReportPeriodSelection={this.handleReportPeriodSelection}
            defaultReportPeriod={this.state.defaultReportPeriod}
            reportPeriods={this.state.reportPeriods}
            noDataForReportPeriod={noData}
          />
        )}

        {noData && !noReit ? (
          <div className="mt-3">
            No REITs found for the selected report period.
          </div>
        ) : (
          <>
            <SignOffTable
              bulkProcessManagementData={this.state.bulkProcessManagementData}
              handleGridChanges={this.handleGridChanges}
              gridColumnState={this.state.gridColumnState}
              gridCurrentState={this.state.gridCurrentState}
              handleGridColumnStateChange={this.handleGridColumnStateChange}
            />

            <BulkSignOffModal
              showSignOffModal={this.state.showSignOffModal}
              toggleShowSignOffModal={this.toggleShowSignOffModal}
              handleSignOffSave={this.handleSignOffSave}
              resetSignOffRadioButtons={this.state.resetSignOffRadioButtons}
              currentUserIsEngagementUser={
                this.props.currentUserIsEngagementUser
              }
            />
          </>
        )}
      </div>
    );
  }
}

SignOffContainer.propTypes = {
  clientId: PropTypes.number,
  commonActions: PropTypes.object,
  bulkProcessManagementData: PropTypes.array,
  client: PropTypes.object,
  currentUserIsEngagementUser: PropTypes.bool,
};

/**
 * Maps items from state to properties of the component
 * @param {Object} state The state
 * @returns {Object} An object containing properties that the component can access
 */
function mapStateToProps(state, ownProps) {
  let cid = Number.parseInt(ownProps.router.params.clientId);
  if (isNaN(cid)) {
    cid = 0;
  }

  return {
    clientId: cid,
    bulkProcessManagementData: state.bulkProcessManagementData,
    isAudit: state.client.isAudit,
    currentUserIsEngagementUser: authActions.isUser(
      state.currentUserAuthorizations,
      cid
    ),
  };
}

/**
 * Binds actions to the dispatcher
 * @param {Object} dispatch The action dispatcher
 * @returns {Object} An object containing properties that the component can access
 */
function mapDispatchToProps(dispatch) {
  return {
    bulkProcessManagementActions: bindActionCreators(
      bulkProcessManagementActions,
      dispatch
    ),
    commonActions: bindActionCreators(commonActions, dispatch),
    clientActions: bindActionCreators(clientActions, dispatch),
    periodActions: bindActionCreators(periodActions, dispatch),
  };
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(
    reduxForm({
      form: 'signOffForm',
      enableReinitialize: true,
    })(SignOffContainer)
  )
);
